import { SITE, absoluteUrl } from "@/lib/site";

export type PageSeoInput = {
  /** Full document title (appears in the browser tab and SERP). */
  title: string;
  description: string;
  /** Path only, e.g. "/about" or "/". */
  path: string;
  /** Absolute OG image URL. Defaults to the site OG image. */
  image?: string;
  /** Shorter OG title when the tab title is too long. Defaults to `title`. */
  ogTitle?: string;
  /** Shorter OG description. Defaults to `description`. */
  ogDescription?: string;
};

/**
 * Per-page SEO for TanStack Router `head()`.
 * Sets unique title, meta description, canonical, and Open Graph / Twitter tags.
 */
export function pageHead({
  title,
  description,
  path,
  image = SITE.ogImage,
  ogTitle = title,
  ogDescription = description,
}: PageSeoInput) {
  const url = absoluteUrl(path);

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: ogTitle },
      { property: "og:description", content: ogDescription },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
      { name: "twitter:title", content: ogTitle },
      { name: "twitter:description", content: ogDescription },
      { name: "twitter:image", content: image },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

/** Merge page SEO with extra head entries (scripts, preloads, etc.). */
export function mergeHead(
  base: ReturnType<typeof pageHead>,
  extra?: {
    meta?: Array<Record<string, string>>;
    links?: Array<Record<string, string>>;
    scripts?: Array<Record<string, unknown>>;
  },
) {
  return {
    meta: [...base.meta, ...(extra?.meta ?? [])],
    links: [...base.links, ...(extra?.links ?? [])],
    scripts: extra?.scripts ?? [],
  };
}
